/**
 * First-visit popup: shows once per browser (not just per session) after a
 * configurable delay, then never again once the visitor closes it, clicks
 * "No, Thanks", or successfully submits the signup form. Uses localStorage
 * so the dismissal persists across sessions, not just the current tab.
 */
const STORAGE_KEY = 'dynor-first-visit-popup-dismissed';

class FirstVisitPopup extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-role="overlay"]');
    this.closeBtn = this.querySelector('[data-role="close"]');
    this.dismissBtn = this.querySelector('[data-role="dismiss"]');
    this.form = this.querySelector('.fvp__form');

    if (this.#alreadyDismissed()) return;

    const delay = Number(this.dataset.delay) || 3000;
    this.timeoutId = setTimeout(() => this.#open(), delay);

    this.closeBtn?.addEventListener('click', () => this.#dismiss());
    this.dismissBtn?.addEventListener('click', () => this.#dismiss());
    this.overlay?.addEventListener('click', () => this.#dismiss());

    // If the form actually posted (page reloaded with a success state),
    // treat that as a permanent dismissal too.
    if (this.querySelector('.fvp__success')) {
      this.#markDismissed();
    } else {
      this.form?.addEventListener('submit', () => this.#markDismissed());
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#dismiss();
    });
  }

  #alreadyDismissed() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  #markDismissed() {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      // Ignore storage errors (e.g. private browsing) — worst case the
      // popup may reappear on a later visit.
    }
  }

  #open() {
    this.setAttribute('data-open', 'true');
  }

  #dismiss() {
    this.removeAttribute('data-open');
    this.#markDismissed();
    if (this.timeoutId) clearTimeout(this.timeoutId);
  }
}

if (!customElements.get('first-visit-popup')) {
  customElements.define('first-visit-popup', FirstVisitPopup);
}
